import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiCreditCard,
    FiEdit2,
    FiFileText,
    FiLoader,
    FiMapPin,
    FiPhone,
    FiRefreshCw,
    FiTrash2,
    FiUser,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedSupplier,
    deleteSupplier,
    fetchSupplierById,
    selectedSupplierSelector,
    supplierErrorSelector,
    supplierLoadingSelector,
    toggleSupplierStatus,
} from "../../../../../../store/billing/suppliers/supplierSlice";

import "./Supplier.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
              }
            : {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
              }
    ).format(date);
};

const getAddressText = (
    address
) => {
    if (!address) {
        return "-";
    }

    if (
        typeof address ===
        "string"
    ) {
        return address;
    }

    const parts = [
        address?.addressLine1,
        address?.addressLine2,
        address?.city,
        address?.state,
        address?.pincode,
        address?.country,
    ].filter(Boolean);

    return parts.length
        ? parts.join(", ")
        : "-";
};

const getSupplierDisplayName = (
    supplier
) =>
    supplier?.companyName ||
    supplier?.supplierName ||
    "Supplier";

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierViewPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const {
        id,
    } =
        useParams();

    const supplier =
        useSelector(
            selectedSupplierSelector
        );

    const loading =
        useSelector(
            supplierLoadingSelector
        ) || {};

    const errors =
        useSelector(
            supplierErrorSelector
        ) || {};

    const [
        localError,
        setLocalError,
    ] = useState("");

    const [
        actionLoading,
        setActionLoading,
    ] = useState(false);

    /*
    |--------------------------------------------------------------------------
    | LOAD SUPPLIER
    |--------------------------------------------------------------------------
    */

    const loadSupplier =
        useCallback(
            async () => {
                if (!id) {
                    return;
                }

                try {
                    setLocalError("");

                    await dispatch(
                        fetchSupplierById(
                            id
                        )
                    ).unwrap();
                } catch (
                    error
                ) {
                    setLocalError(
                        typeof error ===
                            "string"
                            ? error
                            : error?.message ||
                              "Unable to load supplier."
                    );
                }
            },
            [
                dispatch,
                id,
            ]
        );

    useEffect(
        () => {
            loadSupplier();

            return () => {
                dispatch(
                    clearSelectedSupplier()
                );
            };
        },
        [
            loadSupplier,
            dispatch,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | STATUS
    |--------------------------------------------------------------------------
    */

    const supplierStatus =
        String(
            supplier?.status ||
            "ACTIVE"
        ).toUpperCase();

    const isActive =
        supplierStatus ===
        "ACTIVE";

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const summary =
        useMemo(
            () => ({
                openingBalance:
                    roundMoney(
                        supplier
                            ?.openingBalance
                    ),

                creditLimit:
                    roundMoney(
                        supplier
                            ?.creditLimit
                    ),

                paymentTermDays:
                    toNumber(
                        supplier
                            ?.paymentTermDays
                    ),

                openingBalancePosted:
                    Boolean(
                        supplier
                            ?.openingBalancePosted
                    ),
            }),
            [
                supplier,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | TOGGLE STATUS
    |--------------------------------------------------------------------------
    */

    const handleToggleStatus =
        async () => {
            if (!supplier?._id) {
                return;
            }

            const nextStatus =
                isActive
                    ? "INACTIVE"
                    : "ACTIVE";

            const approved =
                window.confirm(
                    `Change supplier status to ${nextStatus}?`
                );

            if (!approved) {
                return;
            }

            try {
                setActionLoading(
                    true
                );

                setLocalError("");

                await dispatch(
                    toggleSupplierStatus(
                        supplier._id
                    )
                ).unwrap();

                await loadSupplier();
            } catch (
                error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : error?.message ||
                          "Unable to change supplier status."
                );
            } finally {
                setActionLoading(
                    false
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | DELETE
    |--------------------------------------------------------------------------
    */

    const handleDelete =
        async () => {
            if (!supplier?._id) {
                return;
            }

            const approved =
                window.confirm(
                    `Delete supplier "${getSupplierDisplayName(
                        supplier
                    )}"?\n\nIf this supplier has purchase bills or ledger transactions, deletion will be blocked.`
                );

            if (!approved) {
                return;
            }

            try {
                setActionLoading(
                    true
                );

                setLocalError("");

                await dispatch(
                    deleteSupplier(
                        supplier._id
                    )
                ).unwrap();

                history.push(
                    "/billing/suppliers"
                );
            } catch (
                error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : error?.message ||
                          "Unable to delete supplier."
                );
            } finally {
                setActionLoading(
                    false
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (
        loading.details &&
        !supplier
    ) {
        return (
            <div className="supplier-page">
                <div className="supplier-empty">
                    <FiLoader className="supplier-spinner" />

                    <h3>
                        Loading supplier...
                    </h3>
                </div>
            </div>
        );
    }

    return (
        <div className="supplier-page">
            {/* HEADER */}

            <header className="supplier-header">
                <div className="supplier-heading">
                    <button
                        type="button"
                        className="supplier-icon-button"
                        onClick={() =>
                            history.push(
                                "/billing/suppliers"
                            )
                        }
                    >
                        <FiArrowLeft />
                    </button>

                    <span className="supplier-heading-icon">
                        <FiUser />
                    </span>

                    <div>
                        <small>
                            SUPPLIER MASTER
                        </small>

                        <h1>
                            {getSupplierDisplayName(
                                supplier
                            )}
                        </h1>

                        <p>
                            {supplier
                                ?.supplierCode ||
                                "-"}
                        </p>
                    </div>
                </div>

                <div className="supplier-header-actions">
                    <button
                        type="button"
                        className="supplier-button"
                        disabled={
                            loading.details ||
                            actionLoading
                        }
                        onClick={
                            loadSupplier
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.details
                                    ? "supplier-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="supplier-button"
                        disabled={
                            actionLoading
                        }
                        onClick={() =>
                            history.push(
                                `/billing/suppliers/${id}/edit`
                            )
                        }
                    >
                        <FiEdit2 />

                        Edit
                    </button>

                    <button
                        type="button"
                        className={
                            isActive
                                ? "supplier-button supplier-button-warning"
                                : "supplier-button supplier-button-success"
                        }
                        disabled={
                            actionLoading
                        }
                        onClick={
                            handleToggleStatus
                        }
                    >
                        {actionLoading ? (
                            <FiLoader className="supplier-spinner" />
                        ) : null}

                        {isActive
                            ? "Deactivate"
                            : "Activate"}
                    </button>

                    <button
                        type="button"
                        className="supplier-button supplier-button-danger"
                        disabled={
                            actionLoading
                        }
                        onClick={
                            handleDelete
                        }
                    >
                        <FiTrash2 />

                        Delete
                    </button>
                </div>
            </header>

            {/* ERROR */}

            {(localError ||
                errors.details ||
                errors.toggleStatus ||
                errors.delete) && (
                <div className="supplier-alert">
                    {localError ||
                        errors.details ||
                        errors.toggleStatus ||
                        errors.delete}
                </div>
            )}

            {/* SUMMARY */}

            <section className="supplier-summary-grid">
                <div className="supplier-summary-card supplier-summary-active">
                    <span>
                        Status
                    </span>

                    <strong
                        className={
                            isActive
                                ? "supplier-text-success"
                                : "supplier-text-muted"
                        }
                    >
                        {
                            supplierStatus
                        }
                    </strong>

                    <small>
                        Supplier availability
                    </small>
                </div>

                <div className="supplier-summary-card supplier-summary-opening">
                    <span>
                        Opening Balance
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                .openingBalance
                        )}
                    </strong>

                    <small>
                        {summary
                            .openingBalancePosted
                            ? "Posted to ledger"
                            : "Not posted"}
                    </small>
                </div>

                <div className="supplier-summary-card">
                    <span>
                        Credit Limit
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                .creditLimit
                        )}
                    </strong>

                    <small>
                        Supplier credit limit
                    </small>
                </div>

                <div className="supplier-summary-card">
                    <span>
                        Payment Terms
                    </span>

                    <strong>
                        {summary
                            .paymentTermDays >
                        0
                            ? `${summary.paymentTermDays} Days`
                            : "Immediate"}
                    </strong>

                    <small>
                        Default due terms
                    </small>
                </div>
            </section>

            {/* BASIC DETAILS */}

            <section className="supplier-view-card">
                <div className="supplier-section-title">
                    <h3>
                        Supplier Details
                    </h3>

                    <p>
                        Basic contact and business information.
                    </p>
                </div>

                <div className="supplier-view-info-grid">
                    <div>
                        <span>
                            Supplier Code
                        </span>

                        <strong>
                            {supplier
                                ?.supplierCode ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Supplier Name
                        </span>

                        <strong>
                            {supplier
                                ?.supplierName ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Company Name
                        </span>

                        <strong>
                            {supplier
                                ?.companyName ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Contact Person
                        </span>

                        <strong>
                            {supplier
                                ?.contactPerson ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Mobile
                        </span>

                        <strong>
                            {supplier
                                ?.mobile ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Alternate Mobile
                        </span>

                        <strong>
                            {supplier
                                ?.alternateMobile ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Email
                        </span>

                        <strong>
                            {supplier
                                ?.email ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Created
                        </span>

                        <strong>
                            {formatDate(
                                supplier
                                    ?.createdAt,
                                true
                            )}
                        </strong>
                    </div>
                </div>
            </section>

            {/* TAX */}

            <section className="supplier-view-card">
                <div className="supplier-section-title">
                    <h3>
                        GST & Tax Details
                    </h3>
                </div>

                <div className="supplier-view-info-grid">
                    <div>
                        <span>
                            GST Registered
                        </span>

                        <strong>
                            {supplier
                                ?.isGstRegistered
                                ? "Yes"
                                : "No"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            GST Registration
                        </span>

                        <strong>
                            {supplier
                                ?.gstRegistrationType ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            GSTIN
                        </span>

                        <strong>
                            {supplier
                                ?.gstin ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            PAN
                        </span>

                        <strong>
                            {supplier
                                ?.pan ||
                                "-"}
                        </strong>
                    </div>
                </div>
            </section>

            {/* ADDRESS */}

            <section className="supplier-view-card">
                <div className="supplier-section-title">
                    <h3>
                        Address
                    </h3>
                </div>

                <div className="supplier-address-grid">
                    <div className="supplier-address-box">
                        <div className="supplier-address-title">
                            <FiMapPin />

                            <strong>
                                Billing Address
                            </strong>
                        </div>

                        <p>
                            {getAddressText(
                                supplier
                                    ?.billingAddress
                            )}
                        </p>
                    </div>

                    <div className="supplier-address-box">
                        <div className="supplier-address-title">
                            <FiMapPin />

                            <strong>
                                Shipping Address
                            </strong>
                        </div>

                        <p>
                            {supplier
                                ?.sameAsBillingAddress
                                ? "Same as billing address"
                                : getAddressText(
                                      supplier
                                          ?.shippingAddress
                                  )}
                        </p>
                    </div>
                </div>
            </section>

            {/* BANK */}

            <section className="supplier-view-card">
                <div className="supplier-section-title">
                    <h3>
                        Bank Details
                    </h3>
                </div>

                <div className="supplier-view-info-grid">
                    <div>
                        <span>
                            Account Holder
                        </span>

                        <strong>
                            {supplier
                                ?.bankDetails
                                ?.accountHolderName ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Bank Name
                        </span>

                        <strong>
                            {supplier
                                ?.bankDetails
                                ?.bankName ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Account Number
                        </span>

                        <strong>
                            {supplier
                                ?.bankDetails
                                ?.accountNumber ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            IFSC
                        </span>

                        <strong>
                            {supplier
                                ?.bankDetails
                                ?.ifscCode ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Branch
                        </span>

                        <strong>
                            {supplier
                                ?.bankDetails
                                ?.branchName ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            UPI ID
                        </span>

                        <strong>
                            {supplier
                                ?.bankDetails
                                ?.upiId ||
                                "-"}
                        </strong>
                    </div>
                </div>
            </section>

            {/* PAYMENT */}

            <section className="supplier-view-card">
                <div className="supplier-section-title">
                    <h3>
                        Payment Information
                    </h3>
                </div>

                <div className="supplier-view-info-grid">
                    <div>
                        <span>
                            Payment Terms
                        </span>

                        <strong>
                            {summary
                                .paymentTermDays >
                            0
                                ? `${summary.paymentTermDays} days`
                                : "Immediate"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Credit Limit
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    .creditLimit
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Opening Balance
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    .openingBalance
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Opening Balance Date
                        </span>

                        <strong>
                            {formatDate(
                                supplier
                                    ?.openingBalanceDate
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Ledger Posted
                        </span>

                        <strong>
                            {supplier
                                ?.openingBalancePosted
                                ? "Yes"
                                : "No"}
                        </strong>
                    </div>
                </div>
            </section>

            {/* QUICK ACTIONS */}

            <section className="supplier-view-card">
                <div className="supplier-section-title">
                    <h3>
                        Billing Actions
                    </h3>

                    <p>
                        Continue billing activity for this supplier.
                    </p>
                </div>

                <div className="supplier-quick-actions">
                    <button
                        type="button"
                        onClick={() =>
                            history.push(
                                `/billing/purchase-bills/create?supplierId=${supplier?._id || ""}`
                            )
                        }
                    >
                        <FiFileText />

                        New Purchase Bill
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            history.push(
                                `/billing/payment-out?supplierId=${supplier?._id || ""}`
                            )
                        }
                    >
                        <FiCreditCard />

                        Make Payment
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            history.push(
                                `/billing/supplier-ledger?supplierId=${supplier?._id || ""}`
                            )
                        }
                    >
                        <FiFileText />

                        View Ledger
                    </button>
                </div>
            </section>

            {/* NOTES */}

            {supplier?.notes && (
                <section className="supplier-view-card">
                    <div className="supplier-section-title">
                        <h3>
                            Notes
                        </h3>
                    </div>

                    <div className="supplier-note-box">
                        {
                            supplier.notes
                        }
                    </div>
                </section>
            )}
        </div>
    );
};

export default SupplierViewPage;